import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import {
    fetchRestaurantOrderById,
    fetchRestaurantPayments,
    fetchOrderById,
    mapPosOrderFromApi,
} from './orderApi';
import { fetchRestaurantById } from './restaurantApi';
import { formatMobileDisplayOrFallback, resolveDisplayOrderNo } from './formatDisplay';
import './css/Receipt.css';

const RECEIPT_STORAGE_KEY = 'receipt_orderData';
const RECEIPT_ORDER_ID_KEY = 'receipt_orderId';
const RECEIPT_RESTAURANT_ID_KEY = 'receipt_restaurantId';
const DEFAULT_RESTAURANT_ID = '113';

function formatRestaurantLocality(restaurant) {
    if (!restaurant || typeof restaurant !== 'object') return 'N/A';

    const addresses = Array.isArray(restaurant.addresses)
        ? restaurant.addresses
        : Array.isArray(restaurant.Addresses)
          ? restaurant.Addresses
          : restaurant.Address
            ? [restaurant.Address]
            : [];

    const primary = addresses[0];
    if (primary && typeof primary === 'object') {
        const parts = [
            primary.city ?? primary.City,
            primary.state ?? primary.State,
            primary.postalCode ?? primary.PostalCode ?? primary.postal_code,
        ]
            .map((part) => (part != null ? String(part).trim() : ''))
            .filter(Boolean);
        if (parts.length > 0) return parts.join(', ');
    }

    return 'N/A';
}

const Receipt = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [orderData, setOrderData] = useState(null);
    const [restaurantData, setRestaurantData] = useState(null);
    const [restaurantReady, setRestaurantReady] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const buildReceiptFromPosOrder = (singleOrder, payment, orderId, rid) => {
        const mapped = mapPosOrderFromApi(singleOrder);
        const lineItems = mapped.lineItems || mapped.orderLineItems || [];
        const items = lineItems.map((li) => ({
            foodItemName: li.foodItemName ?? 'N/A',
            foodQuantity: li.foodQuantity ?? 1,
            quantity: li.foodQuantity ?? 1,
            price: parseFloat(li.price ?? 0),
        }));
        const subtotal = items.reduce(
            (sum, i) => sum + (i.foodQuantity || i.quantity) * (i.price || 0),
            0
        );
        const totalFromApi = parseFloat(mapped.totalPrice ?? 0) || subtotal;
        const gst = totalFromApi - subtotal;
        return {
            orderId: mapped.orderId ?? orderId,
            orderNumber:
                mapped.displayOrderNo ??
                mapped.display_order_no ??
                mapped.orderId ??
                orderId,
            displayOrderNo: mapped.displayOrderNo ?? mapped.display_order_no ?? null,
            mobile: mapped.customerPhone ?? null,
            tableNumber: mapped.tableNumber ?? null,
            paymentMethod:
                payment?.paymentMethod ??
                payment?.PaymentMethod ??
                mapped.payment?.paymentMethod ??
                'CASH',
            billNo:
                mapped.displayOrderNo ??
                mapped.display_order_no ??
                mapped.orderId ??
                orderId,
            items,
            subtotal,
            gst: Number.isFinite(gst) && gst >= 0 ? gst : subtotal * 0.025,
            total: totalFromApi,
            gstPercentage: 2.5,
            restaurantId: mapped.restaurantId ?? rid,
            restaurantName: mapped.restaurantName ?? null,
            createdDate: mapped.createdDate,
            transactionId: payment?.transactionId ?? payment?.TransactionId ?? null,
            bankRRN: payment?.bankRRN ?? payment?.BankRRN ?? null,
            orderType: 'offline',
        };
    };

    // Dine-in receipt: GET /restaurants/:id/orders + /restaurants/:id/payments
    const fetchOrderAndPaymentFromApi = async (orderId, restaurantId) => {
        const rid = restaurantId || DEFAULT_RESTAURANT_ID;
        try {
            const singleOrder = await fetchRestaurantOrderById(rid, orderId);
            if (singleOrder?.orderId != null) {
                let payment = singleOrder.payment ?? null;
                if (!payment) {
                    try {
                        const paymentsData = await fetchRestaurantPayments(rid);
                        const paymentsList = paymentsData?.payments ?? [];
                        payment = paymentsList.find(
                            (p) => Number(p.orderId ?? p.Order_Id) === Number(orderId)
                        );
                    } catch (e) {
                        // ignore
                    }
                }
                return buildReceiptFromPosOrder(singleOrder, payment, orderId, rid);
            }
            setError('Order not found');
            return null;
        } catch (err) {
            console.error('Receipt API fetch error:', err?.response?.data || err);
            setError(err?.message || 'Failed to load order');
            return null;
        }
    };

    useEffect(() => {
        let cancelled = false;

        const loadOrderData = async () => {
            setLoading(true);
            setError(null);

            if (location.state?.orderData) {
                const data = location.state.orderData;
                if (!cancelled) setOrderData(data);
                try {
                    sessionStorage.setItem(RECEIPT_STORAGE_KEY, JSON.stringify(data));
                    if (data.orderId) sessionStorage.setItem(RECEIPT_ORDER_ID_KEY, String(data.orderId));
                    if (data.restaurantId) sessionStorage.setItem(RECEIPT_RESTAURANT_ID_KEY, String(data.restaurantId));
                } catch (e) {}
                setLoading(false);
                return;
            }

            if (location.state?.orderId) {
                const data = await fetchOrderAndPaymentFromApi(location.state.orderId, location.state.restaurantId);
                if (!cancelled) {
                    if (data) {
                        setOrderData(data);
                        try {
                            sessionStorage.setItem(RECEIPT_STORAGE_KEY, JSON.stringify(data));
                        } catch (e) {}
                    } else {
                        navigate('/home?tab=dine-in');
                    }
                }
                setLoading(false);
                return;
            }

            try {
                const stored = sessionStorage.getItem(RECEIPT_STORAGE_KEY);
                if (stored) {
                    const data = JSON.parse(stored);
                    if (!cancelled) setOrderData(data);
                    setLoading(false);
                    return;
                }

                const storedOrderId = sessionStorage.getItem(RECEIPT_ORDER_ID_KEY);
                const storedRestaurantId = sessionStorage.getItem(RECEIPT_RESTAURANT_ID_KEY);
                if (storedOrderId) {
                    const data = await fetchOrderAndPaymentFromApi(storedOrderId, storedRestaurantId);
                    if (!cancelled && data) {
                        setOrderData(data);
                    } else if (!cancelled) {
                        navigate('/home?tab=dine-in');
                    }
                } else if (!cancelled) {
                    navigate('/home?tab=dine-in');
                }
            } catch (e) {
                if (!cancelled) navigate('/home?tab=dine-in');
            }
            if (!cancelled) setLoading(false);
        };

        loadOrderData();
        return () => { cancelled = true; };
    }, [location.state, navigate]);

    // Enrich Bill Num from API display order number (do not fall back to internal orderId here).
    useEffect(() => {
        if (!orderData?.orderId) return;
        const rid = orderData.restaurantId || localStorage.getItem('restaurantId');
        if (!rid) return;
        const hasRealDisplay =
            orderData.displayOrderNo &&
            String(orderData.displayOrderNo) !== String(orderData.orderId);
        if (hasRealDisplay) return;

        let cancelled = false;
        (async () => {
            let apiOrder = null;
            try {
                apiOrder = await fetchOrderById(orderData.orderId);
            } catch {
                apiOrder = null;
            }
            if (!apiOrder && rid) {
                try {
                    apiOrder = await fetchRestaurantOrderById(rid, orderData.orderId);
                } catch {
                    apiOrder = null;
                }
            }
            if (cancelled || !apiOrder?.orderId) return;
            const displayNo =
                apiOrder.displayOrderNo ??
                apiOrder.display_order_no ??
                null;
            if (!displayNo || String(displayNo) === String(apiOrder.orderId)) return;
            setOrderData((prev) =>
                prev
                    ? {
                          ...prev,
                          billNo: displayNo,
                          displayOrderNo: displayNo,
                          total: parseFloat(apiOrder.totalPrice) || prev.total,
                      }
                    : prev
            );
        })();
        return () => { cancelled = true; };
    }, [orderData?.orderId, orderData?.displayOrderNo, orderData?.billNo, orderData?.restaurantId]);

    useEffect(() => {
        const fetchRestaurantData = async () => {
            setRestaurantReady(false);
            try {
                const rid =
                    orderData?.restaurantId ||
                    localStorage.getItem('restaurantId') ||
                    null;

                if (rid) {
                    const restaurant = await fetchRestaurantById(rid);
                    if (restaurant) {
                        setRestaurantData(restaurant);
                        return;
                    }
                }

                const user = JSON.parse(localStorage.getItem('user') || 'null');
                const userId = user?.userId;
                const token = localStorage.getItem('Token');
                if (userId && token) {
                    const response = await axiosRestaurantInstance.get(
                        RESTAURANT_API_PATHS.restaurantUserDefault(userId),
                        {
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );
                    if (response.data) {
                        setRestaurantData(response.data);
                    }
                }
            } catch (error) {
                console.error('Error fetching restaurant data:', error);
            } finally {
                setRestaurantReady(true);
            }
        };

        if (orderData) {
            fetchRestaurantData();
        }
    }, [orderData]);

    useEffect(() => {
        if (!orderData || loading || !restaurantReady) return undefined;

        // Auto print after restaurant address is ready, then route back
        const timer = setTimeout(() => {
            window.print();

            const returnTo = location.state?.returnTo;
            if (returnTo) {
                navigate(returnTo, { state: location.state?.returnState || null });
                return;
            }

            const rawType = (orderData && orderData.orderType) ? String(orderData.orderType).toLowerCase() : '';
            const orderType =
                rawType === 'offline' ? 'offline'
                : rawType === 'catering' ? 'catering'
                : 'online';

            const search = `?tab=orders&orderType=${orderType}`;
            navigate(`/home${search}`, { state: { activeTab: 'orders' } });
        }, 400);
        return () => clearTimeout(timer);
    }, [navigate, orderData, loading, restaurantReady, location.state]);

    if (loading) {
        return <div className="receipt-loading">Loading receipt...</div>;
    }
    if (error) {
        return (
            <div className="receipt-error">
                <p>{error}</p>
                <button onClick={() => navigate('/place-order')}>Back to Place Order</button>
            </div>
        );
    }
    if (!orderData) {
        return null;
    }

    const { orderId, orderNumber, mobile, tableNumber, paymentMethod, items, subtotal, gst, total, gstPercentage, restaurantId, createdDate, transactionId, bankRRN, billNo, displayOrderNo } = orderData;

    // Format date and time - use createdDate from API when available
    const formatDateTime = () => {
        const dateToUse = createdDate ? new Date(createdDate) : new Date();
        if (isNaN(dateToUse.getTime())) return new Date().toLocaleString();
        const day = String(dateToUse.getDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[dateToUse.getMonth()];
        const year = dateToUse.getFullYear();
        const hours = String(dateToUse.getHours()).padStart(2, '0');
        const minutes = String(dateToUse.getMinutes()).padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}`;
    };

    // Calculate totals — GST is split equally as CGST + SGST (intra-state)
    const subTotalVal =
        typeof subtotal === 'number'
            ? subtotal
            : items
              ? items.reduce(
                    (s, i) =>
                        s +
                        ((i.foodQuantity || i.quantity) || 0) * (parseFloat(i.price) || 0),
                    0
                )
              : 0;
    const gstTotal =
        typeof gst === 'number' && Number.isFinite(gst)
            ? Math.max(0, gst)
            : Math.max(0, subTotalVal * 0.05);
    const cgstVal = Math.round((gstTotal / 2) * 100) / 100;
    const sgstVal = Math.round((gstTotal - cgstVal) * 100) / 100;
    const cgstRate =
        subTotalVal > 0 ? Math.round((cgstVal / subTotalVal) * 1000) / 10 : 2.5;
    const sgstRate =
        subTotalVal > 0 ? Math.round((sgstVal / subTotalVal) * 1000) / 10 : 2.5;
    const serviceChargeVal =
        typeof orderData?.serviceCharge === 'number' && Number.isFinite(orderData.serviceCharge)
            ? Math.max(0, orderData.serviceCharge)
            : 0;
    const totalQty = items
        ? items.reduce((sum, item) => sum + (parseInt(item.foodQuantity || item.quantity, 10) || 0), 0)
        : 0;
    const finalTotal =
        typeof total === 'number' && Number.isFinite(total)
            ? total
            : subTotalVal + gstTotal + serviceChargeVal;

    // Get restaurant info from the order's restaurant (not a hardcoded fallback address)
    const restaurantName =
        restaurantData?.restaurantName ||
        restaurantData?.RestaurantName ||
        restaurantData?.Restaurant_Name ||
        orderData?.restaurantName ||
        'Restaurant';

    const restaurantLocality = formatRestaurantLocality(restaurantData);
    const restaurantPhone = formatMobileDisplayOrFallback(
        restaurantData?.contactNo ?? restaurantData?.ContactNo,
        'N/A'
    );
    const restaurantGSTIN =
        restaurantData?.GSTIN ||
        restaurantData?.gstin ||
        restaurantData?.fssaiNumber ||
        'N/A';

    // Prefer API display order number for Bill Num; keep internal Order ID separate.
    const isBackendBillNo = billNo && typeof billNo === 'string' && billNo.startsWith('BILL-');
    const restaurantCode =
        (orderData?.restaurantId && String(orderData.restaurantId)) ||
        (restaurantData?.RestaurantId && String(restaurantData.RestaurantId)) ||
        DEFAULT_RESTAURANT_ID;
    let billNumber;
    const preferredDisplay =
        (displayOrderNo && String(displayOrderNo) !== String(orderId) ? displayOrderNo : null) ||
        (billNo &&
        !isBackendBillNo &&
        String(billNo) !== String(orderId) &&
        String(billNo) !== String(orderNumber)
            ? billNo
            : null) ||
        (orderNumber && String(orderNumber) !== String(orderId) ? orderNumber : null);
    if (preferredDisplay) {
        billNumber = preferredDisplay;
    } else if (isBackendBillNo) {
        const parts = billNo.split('-'); // ["BILL", "113", "YYYYMMDD", "NNNN"]
        if (parts.length === 4) {
            billNumber = `BILL-${restaurantCode}-${parts[2]}-${parts[3]}`;
        } else {
            billNumber = billNo;
        }
    } else {
        billNumber = resolveDisplayOrderNo(
            { displayOrderNo, billNo, orderNumber, orderId },
            orderId != null ? String(orderId) : 'N/A'
        );
    }

    const paymentTypeDisplay = paymentMethod
        ? Array.isArray(paymentMethod)
            ? paymentMethod.map((p) => String(p).toUpperCase()).join(', ')
            : String(paymentMethod).toUpperCase()
        : 'CASH';

    return (
        <div className="receipt-page">
            <div className="receipt-container">
                {/* Restaurant Header */}
                <div className="receipt-restaurant-header">
                    <h1 className="restaurant-name-receipt">{restaurantName}</h1>
                    <div className="restaurant-contact-info">
                        <div className="contact-info-line">{restaurantLocality}</div>
                        <div className="contact-info-line">Phone: {restaurantPhone}</div>
                        <div className="contact-info-line">GSTIN: {restaurantGSTIN}</div>
                    </div>
                </div>

                {/* Bill Details */}
                <div className="bill-details-section">
                    <div className="bill-detail-row bill-detail-row--split">
                        <span className="bill-detail-pair">
                            <span className="bill-label">Bill Num:</span>
                            <span className="bill-value">{billNumber}</span>
                        </span>
                        <span className="bill-detail-pair">
                            <span className="bill-label">Order ID:</span>
                            <span className="bill-value">{orderId || orderNumber || 'N/A'}</span>
                        </span>
                    </div>
                    <div className="bill-detail-row bill-detail-row--split">
                        <span className="bill-detail-pair">
                            <span className="bill-label">PT:</span>
                            <span className="bill-value">{paymentTypeDisplay}</span>
                        </span>
                        <span className="bill-detail-pair">
                            <span className="bill-label">Date:</span>
                            <span className="bill-value">{formatDateTime()}</span>
                        </span>
                    </div>
                    {tableNumber && (
                        <div className="bill-detail-row">
                            <span className="bill-label">Table Number:</span>
                            <span className="bill-value">{tableNumber}</span>
                        </div>
                    )}
                    {transactionId && (
                        <div className="bill-detail-row">
                            <span className="bill-label">Transaction ID:</span>
                            <span className="bill-value">{transactionId}</span>
                        </div>
                    )}
                    {bankRRN && (
                        <div className="bill-detail-row">
                            <span className="bill-label">Bank RRN:</span>
                            <span className="bill-value">{bankRRN}</span>
                        </div>
                    )}
                </div>

                {/* Items Table */}
                <table className="receipt-items-table">
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th>Qty.</th>
                            <th>Rate</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items && items.length > 0 ? (
                            items.map((item, index) => (
                                <tr key={index}>
                                    <td className="item-name-cell">{item.foodItemName || item.name}</td>
                                    <td className="item-qty-cell">{item.foodQuantity || item.quantity}</td>
                                    <td className="item-rate-cell">₹{parseFloat(item.price || 0).toFixed(2)}</td>
                                    <td className="item-total-cell">₹{((item.foodQuantity || item.quantity) * parseFloat(item.price || 0)).toFixed(2)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="no-items">No items found</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* Summary Section */}
                <div className="receipt-summary-section">
                    <div className="summary-row-receipt">
                        <span className="summary-label-receipt">Sub Total:</span>
                        <span className="summary-value-receipt">₹{subTotalVal.toFixed(2)}</span>
                    </div>
                    <div className="summary-row-receipt">
                        <span className="summary-label-receipt">CGST@{cgstRate}:</span>
                        <span className="summary-value-receipt">₹{cgstVal.toFixed(2)}</span>
                    </div>
                    <div className="summary-row-receipt">
                        <span className="summary-label-receipt">SGST@{sgstRate}:</span>
                        <span className="summary-value-receipt">₹{sgstVal.toFixed(2)}</span>
                    </div>
                    {serviceChargeVal > 0 ? (
                        <div className="summary-row-receipt">
                            <span className="summary-label-receipt">Service Charge:</span>
                            <span className="summary-value-receipt">
                                ₹{serviceChargeVal.toFixed(2)}
                            </span>
                        </div>
                    ) : null}
                    <div className="summary-row-receipt total-row-receipt">
                        <span className="summary-label-receipt">Total:</span>
                        <span className="summary-value-receipt total-amount-receipt">
                            ₹{finalTotal.toFixed(2)}
                        </span>
                    </div>
                </div>

                {/* Footer Message */}
                <div className="receipt-footer-message">
                    <p>Thank You, Visit Again</p>
                </div>
            </div>
        </div>
    );
};

export default Receipt;
